import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import TopMenu from './TopMenu';

// 5.23.0: top-bar ROW 2 — exactly these buttons, all one width:
//   [+ New ▾] [Renewals] [Bonus] [ACORD] [Books] [HealthSherpa]  —flexible gap—  [Admin ▾ + dot] [FAQ] [InsureIQ] [Logout]
// 5.24.0: FAQ (all users) sits directly left of InsureIQ; admins see an open-conflict count badge on it.
// The common width is the widest visible label (measured, so it follows the real font). If the full
// "🩺 HealthSherpa" doesn't fit the row at that width, HealthSherpa shows "HSherpa" (title keeps the
// full name); below that the row wraps instead of scrolling. Hidden buttons (Admin for non-admins) leave
// no gap. Visibility rules are today's: New Cabinet / Users / Backups / Trash admin-only, the rest all.

const GAP = 10;         // between buttons
const SPLIT = 24;       // minimum flexible gap between the two groups
const PAD_X = 28;       // 14 px left + right, as before

export const BACKUP_TITLES = {
  green: 'Backups: all healthy', amber: 'Backups: attention needed', red: 'Backups: FAILURE — check now',
  stale: 'Backups: status not reporting', unknown: 'Backup status',
};

// The same dot as before 5.23.0: worst status; hollow grey ring = stale; nothing when the poll failed.
export function BackupDot({ summary, testId }) {
  if (!summary) return null;
  if (summary.level === 'stale') {
    return <span data-testid={testId} data-level="stale" style={{ width: 9, height: 9, borderRadius: '50%', border: '2px solid #bbb', background: 'transparent', display: 'inline-block', boxSizing: 'border-box', flexShrink: 0 }} />;
  }
  return <span data-testid={testId} data-level={summary.level} style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', flexShrink: 0, background: ({ green: '#2ecc71', amber: '#f1c40f', red: '#e74c3c' }[summary.level] || '#888') }} />;
}

const btn = (bg) => ({
  padding: `7px ${PAD_X / 2}px`, background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
});

export default function TopBarActions({
  user, darkMode, theme, backupSummary, showInsureIQ, showFaq = false, faqOpenConflicts = 0,
  onNewClient, onNewCabinet, onNewSignature, onSignatures,
  onUsers, onBackups, onTrash,
  onRenewals, onBonus, onAcord, onBooks, onHealthSherpa, onFaq, onInsureIQ, onLogout,
}) {
  const isAdmin = user?.role === 'admin';
  const rowRef = useRef(null);
  const fullRef = useRef(null);
  const [width, setWidth] = useState(null);
  const [short, setShort] = useState(false);

  const neutral = darkMode ? '#3a3f47' : '#2f3640';   // + New ▾ / Admin ▾: neutral dark
  const newItems = [
    { key: 'client', label: '+ New Client', onClick: onNewClient },
    ...(isAdmin ? [{ key: 'cabinet', label: '+ New Cabinet', onClick: onNewCabinet }] : []),
    { key: 'sigreq', label: '＋ New signature request', onClick: onNewSignature },
    { key: 'sigs', label: '✍️ Signatures', onClick: onSignatures },
  ];
  const adminItems = isAdmin ? [
    { key: 'users', label: '👥 Users', onClick: onUsers },
    { key: 'backups', label: '💾 Backups', onClick: onBackups, dot: <BackupDot summary={backupSummary} testId="backup-dot-menu" /> },
    { key: 'trash', label: '🗑 Trash', onClick: onTrash },
  ] : [];

  // Measure each visible button's natural content width; one common width = the widest.
  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const labels = Array.from(row.querySelectorAll('[data-topbtn] [data-lbl]'));
    if (!labels.length) return;
    const natural = labels.map((el) => Math.ceil(el.getBoundingClientRect().width) + PAD_X);
    const others = Math.max(...labels.filter((el) => !el.closest('[data-hsbtn]')).map((el) => Math.ceil(el.getBoundingClientRect().width) + PAD_X));
    const fullW = Math.max(others, (fullRef.current ? Math.ceil(fullRef.current.getBoundingClientRect().width) : 0) + PAD_X);
    const n = labels.length;
    const avail = row.clientWidth;
    const fits = (w) => n * w + (n - 2) * GAP + SPLIT <= avail;
    const useShort = avail > 0 && !fits(fullW);
    const w = useShort ? Math.max(...natural.filter((_, i) => !labels[i].closest('[data-hsbtn]')), others, shortWidth(row)) : fullW;
    setShort(useShort);
    setWidth(Math.max(w, 0) || null);
  }, []);

  useLayoutEffect(() => {
    measure();
    const row = rowRef.current;
    let ro;
    if (row && typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(() => measure()); ro.observe(row); }
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => { if (ro) ro.disconnect(); };
  }, [measure, isAdmin, darkMode, faqOpenConflicts]);   // the FAQ badge changes the widest label

  const theButton = (key, bg, label, onClick, title, extra = {}) => (
    <button key={key} type="button" data-topbtn="1" data-testid={`top-${key}`} onClick={onClick} title={title}
      style={{ ...btn(bg), width: width || undefined, ...extra.style }} {...(extra.attrs || {})}>
      <span data-lbl="1" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );

  return (
    <div ref={rowRef} data-testid="topbar-row2"
      style={{ flex: '1 1 100%', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: GAP, rowGap: 8, position: 'relative' }}>
      {/* hidden: the full HealthSherpa label, measured even while "HSherpa" is showing */}
      <span ref={fullRef} aria-hidden="true" style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, pointerEvents: 'none', left: 0, top: 0 }}>🩺 HealthSherpa</span>
      <span data-hsshort-probe="1" aria-hidden="true" style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, pointerEvents: 'none', left: 0, top: 0 }}>🩺 HSherpa</span>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: GAP }}>
        <TopMenu label="+ New ▾" items={newItems} buttonStyle={btn(neutral)} width={width || undefined}
          theme={theme} title="Create: client, cabinet, signature request; Signatures" testId="top-new" />
        {theButton('renewals', '#185FA5', '📋 Renewals', onRenewals, 'Renewals')}
        {theButton('bonus', '#27AE60', '💵 Bonus', onBonus, 'Bonus')}
        {theButton('acord', '#0f766e', '📄 ACORD', onAcord, 'ACORD — insurance form filler')}
        {theButton('books', '#b45309', '📚 Books', onBooks, 'Books — accounting & invoicing')}
        {theButton('healthsherpa', '#0e7c86', short ? '🩺 HSherpa' : '🩺 HealthSherpa', onHealthSherpa,
          'HealthSherpa', { attrs: { 'data-hsbtn': '1' } })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: GAP, marginLeft: 'auto', paddingLeft: SPLIT - GAP }}>
        {isAdmin && (
          <TopMenu label="Admin ▾" items={adminItems} buttonStyle={btn(neutral)} width={width || undefined} theme={theme}
            title={backupSummary ? `Admin — ${BACKUP_TITLES[backupSummary.level] || 'Backup status'}` : 'Admin — Users, Backups, Trash'}
            dot={<BackupDot summary={backupSummary} testId="backup-dot-admin" />} testId="top-admin" />
        )}
        {theButton('faq', showFaq ? '#7c5cbf' : '#16a085',
          <>❓ FAQ{isAdmin && faqOpenConflicts > 0 && (
            <span data-testid="faq-conflict-badge" title={`${faqOpenConflicts} open FAQ conflict(s) to review`}
              style={{ background: '#e74c3c', color: '#fff', borderRadius: 9, padding: '0 6px', fontSize: 11, lineHeight: '16px', minWidth: 16, textAlign: 'center' }}>
              {faqOpenConflicts}
            </span>)}</>,
          onFaq, isAdmin && faqOpenConflicts > 0 ? `FAQ — ${faqOpenConflicts} open conflict(s) to review` : 'FAQ — ask about an insurance company')}
        {theButton('insureiq', showInsureIQ ? '#7c5cbf' : '#4f8ef7', '⚡ InsureIQ', onInsureIQ, 'InsureIQ — AI document assistant')}
        {theButton('logout', '#c0392b', 'Logout', onLogout, 'Log out')}
      </div>
    </div>
  );
}

function shortWidth(row) {
  const el = row.querySelector('[data-hsshort-probe]');
  return el ? Math.ceil(el.getBoundingClientRect().width) + PAD_X : 0;
}
